import React from 'react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Tile from '@atlaskit/tile/tile';
import { token } from '@atlaskit/tokens';

import { SmartLinkIconRenderer } from '../SmartLinkIconRenderer.tsx';
import { getConnectionRowContent } from '../utils/getConnectionRowContent.ts';
import type { EnhancedSmartLink, LinkDirection } from '../utils/smartLinkTypes.ts';
import { getSmartLinkIcon } from '../utils/smartLinkIcon.ts';

const styles = cssMap({
	rowBody: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		flex: 1,
		minWidth: 0,
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
		backgroundColor: 'transparent',
		textAlign: 'left',
		borderRadius: token('radius.small'),
	},
	textColumn: {
		minWidth: 0,
		flex: 1,
	},
	title: {
		font: token('font.body'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	description: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text.subtlest'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	// The summary needs a wrapping context so Text can clamp it to two lines.
	aiSummary: {
		whiteSpace: 'normal',
	},
});

export type ConnectionRowBodyProps = {
	item: EnhancedSmartLink;
	linkDirection: LinkDirection;
	compact?: boolean;
	aiSummary?: string;
	onClick: () => void;
};

export const ConnectionRowBody = ({
	item,
	linkDirection,
	compact,
	aiSummary,
	onClick,
}: ConnectionRowBodyProps): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const { titleContent, descriptionContent } = getConnectionRowContent({
		item,
		linkDirection,
		locale,
		formatMessage,
	});
	const icon = getSmartLinkIcon(item);

	return (
		<Pressable xcss={styles.rowBody} onClick={onClick}>
			<Tile label="" size="small" backgroundColor="transparent" hasBorder>
				{icon ? (
					<SmartLinkIconRenderer icon={icon} size="menu" />
				) : (
					<LinkIcon label="" color={token('color.icon.subtle')} />
				)}
			</Tile>
			<Box xcss={styles.textColumn}>
				{compact ? (
					<Box xcss={styles.title}>{titleContent}</Box>
				) : (
					<Stack space="space.025">
						<Box xcss={styles.title}>{titleContent}</Box>
						{aiSummary ? (
							<Box xcss={styles.aiSummary}>
								<Text as="p" size="small" weight="regular" color="color.text.subtlest" maxLines={2}>
									{aiSummary}
								</Text>
							</Box>
						) : (
							<Box xcss={styles.description}>{descriptionContent}</Box>
						)}
					</Stack>
				)}
			</Box>
		</Pressable>
	);
};
